import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { authAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Car, KeyRound } from 'lucide-react';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const token = searchParams.get('token') || '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error('Passwörter stimmen nicht überein');
      return;
    }
    if (password.length < 8) {
      toast.error('Passwort muss mindestens 8 Zeichen lang sein');
      return;
    }
    setLoading(true);
    try {
      await authAPI.resetPassword({ token, new_password: password });
      toast.success('Passwort erfolgreich zurückgesetzt');
      navigate('/admin/login');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Zurücksetzen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4" data-testid="reset-password-page">
      <Card className="w-full max-w-md shadow-lg border-0 rounded-sm">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-4">
            <div className="w-12 h-12 bg-[#0E518D] rounded-sm flex items-center justify-center">
              <Car className="w-6 h-6 text-white" strokeWidth={1.5} />
            </div>
          </div>
          <CardTitle className="font-heading text-2xl text-slate-900">Neues Passwort</CardTitle>
        </CardHeader>
        <CardContent>
          {!token ? (
            <div className="text-center py-4">
              <p className="text-slate-500 font-body text-sm">Kein Token vorhanden.</p>
              <Link to="/admin/forgot-password" className="text-[#0E518D] underline text-sm font-body mt-2 block">
                Neuen Token anfordern
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="password" className="font-body text-sm">Neues Passwort</Label>
                <Input
                  id="password"
                  data-testid="reset-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1 rounded-sm"
                  required
                  minLength={8}
                />
              </div>
              <div>
                <Label htmlFor="confirm" className="font-body text-sm">Passwort bestätigen</Label>
                <Input
                  id="confirm"
                  data-testid="reset-confirm-input"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="mt-1 rounded-sm"
                  required
                />
              </div>
              <Button
                type="submit"
                data-testid="reset-submit-button"
                className="w-full bg-[#0E518D] hover:bg-[#0B4171] text-white rounded-sm py-3 font-body"
                disabled={loading}
              >
                <KeyRound className="w-4 h-4 mr-2" /> Passwort zurücksetzen
              </Button>
            </form>
          )}
          <div className="mt-4 text-center">
            <Link to="/admin/login" className="text-sm text-[#0E518D] hover:underline font-body">
              Zurück zum Login
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
